import { useMutation } from "@tanstack/react-query"

import type { SpringApi, SpringError } from "./createHttpClient"

const parseError = <TError extends Record<string, unknown>>(result: SpringError<TError>) => {
  if (result.details) {
    const errors = {} as Partial<Record<keyof TError, string[]>>
    result.details.forEach(({ field, message }) => {
      // 配列やオブジェクトなどでネストされたエラーの場合、"array[0]" や "obj.property" のような文字列がfieldに入ります。
      // そのため一番上の階層のフィールドにメッセージを集約しています。
      const topLevelField = field.split(/(\[|\.)/)[0] as keyof TError
      errors[topLevelField] ??= []
      errors[topLevelField]?.push(message)
    })
    return {
      detailErrorMessages: errors,
      allErrorMessages: Object.values(errors as Record<string, string[]>).flat(),
    }
  }
  return {
    errorMessage: result.message,
    detailErrorMessages: {},
    allErrorMessages: [result.message],
  }
}

type MutationResult<TArg, TSuccess, TError> = {
  mutate: (arg: TArg) => void
  reset: () => void
  isLoading: boolean
  result: TSuccess | undefined
  errorMessage?: string
  detailErrorMessages: Partial<Record<keyof TError, string[]>>
  allErrorMessages?: string[]
}

const useMutationApi = <TArg, TSuccess, TError extends Record<string, unknown>>(
  api: SpringApi<TArg, TSuccess, TError>,
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: (data: TSuccess, variables: TArg) => void
    onError?: (data: SpringError<TError>) => void
  } = {}
): MutationResult<TArg, TSuccess, TError> => {
  const { isLoading, mutate, data, reset } = useMutation(api, {
    onSuccess(result, variables) {
      if (result.isSuccess) {
        onSuccess?.(result.data, variables)
      } else {
        onError?.(result.data)
      }
    },
  })

  return {
    mutate,
    reset,
    isLoading,
    result: data?.isSuccess ? data.data : undefined,
    ...(data?.isError && !isLoading ? parseError(data.data) : { detailErrorMessages: {} }),
  }
}

export default useMutationApi
